import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {ANTIBODY_REST_PROPS_BY_ID} from 'antibodies/AntibodyConstants';
import AntibodyFeedbackBanner from 'antibodies/AntibodyFeedbackBanner';
import {formatToSortedDistinctDomains} from 'antibodies/AntibodyUtils';
import {useAntibodyFormatsQuery} from 'antibodies/hooks/useAntibodyFormatsQuery';
import AntibodyConfigurationTable from 'antibodies/schema-configuration/AntibodyConfigurationTable';
import AntibodyFormatPicker from 'antibodies/schema-configuration/AntibodyFormatPicker';
import {
  ANTIBODY_SCHEMA_INTERFACE_ID,
  DraftImplementedAntibodyInterface,
} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import {AntibodyProps} from 'antibodies/visualization/Antibody';
import {assert} from 'Asserts';
import {AntibodyDomainType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {Header3} from 'ui-library/Headers';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {immutableReplace} from 'util/ImmutableListUtil';

import {AntibodyFormatGraphQL} from '../../../graphql-fragments/antibodies/__generated/AntibodyFormatGraphQL';
import {TagSchemaSummaryGraphQL} from '../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';

function findSelectedFormat(
  formatId: null | string,
  formats: Array<AntibodyFormatGraphQL>
): AntibodyFormatGraphQL | null {
  if (formatId == null) {
    return null;
  } else {
    return formats.find(({id}) => id === formatId) ?? null;
  }
}

export interface AntibodyConfigurationProps {
  antibodyInterfaceImplementation: DraftImplementedAntibodyInterface;
  disabled: boolean;
  onChange: (config: DraftImplementedAntibodyInterface) => void;
  /* All tag schemas that currently exist on the registry */
  allSchemas: Array<TagSchemaSummaryGraphQL>;
}

export default function AntibodySchemaConfiguration({
  antibodyInterfaceImplementation,
  disabled,
  onChange,
  allSchemas,
}: AntibodyConfigurationProps): JSX.Element {
  const formats = useAntibodyFormatsQuery();
  const selectedFormat = useMemo(
    () => findSelectedFormat(antibodyInterfaceImplementation.formatId ?? null, formats),
    [formats, antibodyInterfaceImplementation]
  );

  if (formats.length === 0) {
    return <CenteredHorizontalThrobber />;
  }
  const formatsProps: Array<AntibodyProps> = formats.map((format) => {
    const restProps = ANTIBODY_REST_PROPS_BY_ID[format.id];
    return {
      format,
      ...restProps,
    };
  });

  const onChangeFormat = (selectedFormat: string | null): void => {
    const format = findSelectedFormat(selectedFormat, formats);
    if (selectedFormat == null || format == null) {
      onChange({
        interfaceId: ANTIBODY_SCHEMA_INTERFACE_ID,
      });
    } else {
      const sortedDistinctDomains = formatToSortedDistinctDomains(format);
      onChange({
        interfaceId: ANTIBODY_SCHEMA_INTERFACE_ID,
        formatId: selectedFormat,
        domainSchemaFieldSpecs: sortedDistinctDomains.map(({type, specificity}) => ({
          schemaOrInterfaceId: null,
          domainType: type,
          specificity,
        })),
      });
    }
  };

  const onChangeDomainSchema = (
    domainType: AntibodyDomainType,
    specificity: string | null,
    schemaOrInterfaceId: string | null
  ): void => {
    const domainSchemaFieldSpecs = antibodyInterfaceImplementation.domainSchemaFieldSpecs;
    assert(domainSchemaFieldSpecs != null, 'Domain schema field specs and format should be defined');
    const specToReplaceIndex = domainSchemaFieldSpecs.findIndex(
      (spec) => spec.domainType === domainType && spec.specificity === specificity
    );
    const newDomainSchemaFieldSpecs = immutableReplace(domainSchemaFieldSpecs, specToReplaceIndex, {
      schemaOrInterfaceId,
      domainType,
      specificity,
    });
    onChange({
      ...antibodyInterfaceImplementation,
      domainSchemaFieldSpecs: newDomainSchemaFieldSpecs,
    });
  };

  return (
    <>
      <div className={css(styles.formatPickerWrapper)}>
        <AntibodyFeedbackBanner />
        <Header3 className={css(styles.formatPickerHeader)}>Format</Header3>
        <FieldLabel label="Select format" />
        <AntibodyFormatPicker
          antibodyProps={formatsProps}
          disabled={disabled}
          selectedFormat={selectedFormat?.id ?? null}
          setSelectedFormat={onChangeFormat}
        />
        {selectedFormat != null && (
          <AntibodyConfigurationTable
            format={selectedFormat}
            allSchemas={allSchemas}
            disabled={disabled}
            onChangeDomainSchema={onChangeDomainSchema}
            // TODO(BNCH-126332): Domain schema specs should always be defined so data is shown for existing schemas
            domainSchemaFieldSpecs={antibodyInterfaceImplementation.domainSchemaFieldSpecs ?? []}
          />
        )}
      </div>
    </>
  );
}

const styles = StyleSheet.create({
  formatPickerWrapper: {
    borderRadius: '8px',
    backgroundColor: Color.GRAY_50,
    padding: '16px',
    marginBottom: '16px',
  },
  formatPickerHeader: {
    margin: '16px 0',
  },
});
